import { useCallback, useEffect, useMemo } from "react";

import {
  SteppedMultiColumnDropdown,
  type SteppedMultiColumnDropdownConfig,
  SelectField,
} from "@stacklet/ui";
import { graphql, usePaginationFragment } from "react-relay";

import type { JiraInstance_jiraInstances$key } from "./__generated__/JiraInstance_jiraInstances.graphql";

/** Data structure returned in onChange callback */
export interface JiraInstanceData {
  /** Unique identifier for the Jira instance */
  uuid: string;
  /** Jira instance URL */
  url: string;
  /** Username for basic auth instances */
  username?: string | null;
  /** Client ID for service token instances */
  clientId?: string | null;
  /** Soft issue limit — triggers warning when exceeded */
  issueLimitSoft?: number | null;
  /** Hard issue limit — blocks submission when exceeded */
  issueLimitHard?: number | null;
  /** Available spaces in this Jira instance */
  spaces: {
    key: string;
    name: string;
    workTypes: {
      name: string;
      iconUrl: string;
    }[];
  }[];
  /** Default space for this instance (if configured) */
  defaultSpace: {
    key: string;
    name: string;
  } | null;
  /** Default work type for this instance (if configured) */
  defaultWorkType: {
    name: string;
  } | null;
}

/** Props for JiraInstance component */
interface Props {
  /** Relay fragment reference for jira instances data */
  jiraInstancesRef: JiraInstance_jiraInstances$key;
  /** Currently selected instance UUID (if any) */
  selectedInstanceUuid?: string;
  /** Callback when instance selection changes */
  onChange: (instanceData: JiraInstanceData) => void;
  /** Optional callback with all currently loaded instances */
  onInstancesChanged?: (instanceMap: Map<string, JiraInstanceData>) => void;
}

export function JiraInstance({
  jiraInstancesRef,
  selectedInstanceUuid,
  onChange,
  onInstancesChanged,
}: Props) {
  const { data } = usePaginationFragment(
    graphql`
      fragment JiraInstance_jiraInstances on Query
      @argumentDefinitions(
        count: { type: "Int", defaultValue: 50 }
        cursor: { type: "String" }
      )
      @refetchable(queryName: "JiraInstancePaginationQuery") {
        jiraInstances(first: $count, after: $cursor)
          @connection(key: "JiraInstance__jiraInstances") {
          edges {
            node {
              id
              name
              uuid
              url
              username
              clientId
              issueLimitSoft
              issueLimitHard
              spaces {
                key
                name
                workTypes {
                  name
                  iconUrl
                }
              }
              defaultSpace {
                key
                name
              }
              defaultWorkType {
                name
              }
            }
          }
        }
      }
    `,
    jiraInstancesRef,
  );

  // Transform API data to dropdown items
  const items = useMemo(
    () =>
      data.jiraInstances?.edges?.map((edge) => {
        const { url, name, username } = edge.node;
        const title = name ?? url;
        const label = username
          ? `${title} (${username})`
          : `${title} (Service Account)`;
        return { id: edge.node.uuid, name: label };
      }) ?? [],
    [data.jiraInstances?.edges],
  );

  // Create instance lookup map for onChange handler
  const instanceMap = useMemo(() => {
    const map = new Map<string, JiraInstanceData>();
    data.jiraInstances?.edges?.forEach((edge) => {
      map.set(edge.node.uuid, {
        uuid: edge.node.uuid,
        url: edge.node.url,
        username: edge.node.username ?? null,
        clientId: edge.node.clientId,
        issueLimitSoft: edge.node.issueLimitSoft,
        issueLimitHard: edge.node.issueLimitHard,
        spaces: edge.node.spaces.map((space) => ({
          key: space.key,
          name: space.name,
          workTypes: space.workTypes.map((workType) => ({
            name: workType.name,
            iconUrl: workType.iconUrl,
          })),
        })),
        defaultSpace: edge.node.defaultSpace
          ? {
              key: edge.node.defaultSpace.key,
              name: edge.node.defaultSpace.name,
            }
          : null,
        defaultWorkType: edge.node.defaultWorkType
          ? {
              name: edge.node.defaultWorkType.name,
            }
          : null,
      });
    });
    return map;
  }, [data.jiraInstances?.edges]);

  useEffect(() => {
    onInstancesChanged?.(instanceMap);
  }, [instanceMap, onInstancesChanged]);

  // Single column configuration with dynamic items
  const config: SteppedMultiColumnDropdownConfig = useMemo(
    () => [
      [
        {
          type: "list",
          title: "Select Jira Instance",
          items,
          flex: true,
        },
      ],
    ],
    [items],
  );

  const handleChange = useCallback(
    (selections: string[]) => {
      const instanceUuid = selections[0];
      if (instanceUuid) {
        const instanceData = instanceMap.get(instanceUuid);
        if (instanceData) {
          onChange(instanceData);
        }
      }
    },
    [instanceMap, onChange],
  );

  const trigger = ({ isOpen }: { isOpen: boolean }) => (
    <SelectField
      className="w-[375px]"
      isOpen={isOpen}
      value={
        selectedInstanceUuid
          ? items.find((item) => item.id === selectedInstanceUuid)?.name ||
            "Select Instance"
          : "Select Instance"
      }
    />
  );

  return (
    <SteppedMultiColumnDropdown
      config={config}
      initialSelections={
        selectedInstanceUuid ? [selectedInstanceUuid] : undefined
      }
      onChange={handleChange}
      trigger={trigger}
      autoHeight
    />
  );
}
